import { useEffect, useRef } from "react";
import { useProvider } from "../../../../../../queries/use-provider";

interface Params {
  /** Selected provider uuid (empty/undefined when no provider is chosen). */
  providerUuid?: string;
  tenantId?: string;
  /** Current NPI value held in the form. */
  currentValue?: string;
  /** Setter that writes the resolved NPI back into the form. */
  setValue: (npi: string) => void;
}

/**
 * Auto-fills a provider's NPI field on the Create/Update Claim form (ECRH-5217).
 *
 * When a provider is selected, its NPI is fetched from the provider profile
 * (getProviderById, since the lightweight dropdown list omits NPI) and written
 * into the form. The value stays editable:
 *  - an explicit user selection (via `markSelected`) overwrites the NPI, and
 *  - an empty field is filled once the provider data arrives (covers a
 *    pre-populated provider on an existing/new claim),
 *  - but a manually edited or already-persisted NPI is never overwritten on its
 *    own, and clearing the field does not re-fill it.
 */
export function useProviderNpiAutofill({
  providerUuid,
  tenantId,
  currentValue,
  setValue,
}: Params) {
  const { data: providerRes } = useProvider({
    providerUuid: providerUuid ?? null,
    tenantId,
  });
  const forceUuidRef = useRef<string | null>(null);
  // The provider profile carries `npi`; the generated return type is generic, so
  // narrow through `any` as the other provider-profile consumers do.
  const npi = (providerRes?.data as any)?.npi;

  useEffect(() => {
    if (!providerUuid || npi === undefined || npi === null) return;
    const force = forceUuidRef.current === providerUuid;
    if (force || !currentValue) {
      setValue(String(npi));
      if (force) forceUuidRef.current = null;
    }
    // `currentValue` is intentionally excluded from deps: we only react to a new
    // provider fetch/selection, never re-fill after the user edits or clears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerUuid, npi]);

  return {
    /**
     * Call from the provider <select> onChange so a fresh pick overwrites the
     * NPI even if one was already present.
     */
    markSelected: (uuid: string | null) => {
      forceUuidRef.current = uuid;
    },
  };
}
